"use client";

import * as React from "react";
import { Camera, ExternalLink, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  AGENT_PROFILE_PRESETS,
  parseAgentPresetLabel,
  presetFromUrl,
} from "~/lib/onboarding/agent-profile-presets";
import type { HireRecommendationsAgentProfileDraft } from "~/components/marketplace/hire-recommendations-overview-step";

type RecommendedAgentProfilePreviewProps = {
  businessName: string;
  websiteUrl?: string;
  agentProfile: HireRecommendationsAgentProfileDraft;
  onAgentProfileChange: (next: HireRecommendationsAgentProfileDraft) => void;
  isGeneratingPersona?: boolean;
  hideInlineAbout?: boolean;
  personaModalOpen?: boolean;
  onPersonaModalOpenChange?: (open: boolean) => void;
};

function notificationsEmailDomain(websiteUrl?: string): string {
  const display = websiteUrl?.replace(/^https?:\/\//i, "").replace(/\/$/, "") ?? "";
  const domain = display.split("/")[0]?.replace(/^www\./i, "") ?? "yourcompany.com";
  return `notifications@${domain || "yourcompany.com"}`;
}

export function RecommendedAgentProfilePreview({
  businessName,
  websiteUrl,
  agentProfile,
  onAgentProfileChange,
  isGeneratingPersona = false,
  hideInlineAbout = false,
  personaModalOpen: personaModalOpenProp,
  onPersonaModalOpenChange,
}: RecommendedAgentProfilePreviewProps) {
  const [profileModalOpen, setProfileModalOpen] = React.useState(false);
  const [personaModalOpenInternal, setPersonaModalOpenInternal] = React.useState(false);
  const [imageModalOpen, setImageModalOpen] = React.useState(false);
  const [isUploadingImage, setIsUploadingImage] = React.useState(false);

  const personaModalOpen = personaModalOpenProp ?? personaModalOpenInternal;
  const setPersonaModalOpen = onPersonaModalOpenChange ?? setPersonaModalOpenInternal;

  const [draftName, setDraftName] = React.useState(agentProfile.agentName);
  const [draftTitle, setDraftTitle] = React.useState(agentProfile.jobTitle);
  const [draftPersona, setDraftPersona] = React.useState(agentProfile.persona);
  const [draftImageUrl, setDraftImageUrl] = React.useState(agentProfile.profileImageUrl);

  React.useEffect(() => {
    setDraftName(agentProfile.agentName);
    setDraftTitle(agentProfile.jobTitle);
    setDraftPersona(agentProfile.persona);
    setDraftImageUrl(agentProfile.profileImageUrl);
  }, [agentProfile]);

  React.useEffect(() => {
    if (personaModalOpen) {
      setDraftPersona(agentProfile.persona);
    }
  }, [personaModalOpen, agentProfile.persona]);

  const selectedPreset =
    presetFromUrl(agentProfile.profileImageUrl) ?? AGENT_PROFILE_PRESETS[0]!;
  const draftPreset = presetFromUrl(draftImageUrl);
  const draftPresetName = draftPreset
    ? parseAgentPresetLabel(draftPreset.label).agentName
    : "Custom upload";
  const selectValue = draftPreset?.url ?? (draftImageUrl || selectedPreset.url);

  const normalizedWebsite = websiteUrl?.trim()
    ? websiteUrl.startsWith("http")
      ? websiteUrl
      : `https://${websiteUrl}`
    : "";
  const websiteDisplay =
    websiteUrl?.replace(/^https?:\/\//i, "").replace(/\/$/, "") || "your website";
  const emailFromName = agentProfile.jobTitle.trim() || "Agent";
  const notificationEmail = notificationsEmailDomain(websiteUrl);

  const updateProfile = (patch: Partial<HireRecommendationsAgentProfileDraft>) => {
    onAgentProfileChange({ ...agentProfile, ...patch });
  };

  const handleImageUpload = async (file: File) => {
    setIsUploadingImage(true);
    try {
      const response = await fetch(`/api/upload/avatar?filename=${encodeURIComponent(file.name)}`, {
        method: "POST",
        body: file,
      });
      if (!response.ok) throw new Error("Upload failed");
      const result = (await response.json()) as { url?: string };
      if (!result.url) throw new Error("No URL returned");
      setDraftImageUrl(result.url);
      toast.success("Profile image uploaded");
    } catch {
      toast.error("Failed to upload profile image");
    } finally {
      setIsUploadingImage(false);
    }
  };

  return (
    <>
      <div className="rounded-lg border border-[#C3D4FC] bg-[#F0F5FF] p-4">
        <div className="flex items-start gap-4">
          <div className="relative shrink-0">
            <Avatar className="h-20 w-20 border-2 border-white shadow-sm">
              <AvatarImage
                src={agentProfile.profileImageUrl}
                alt={agentProfile.agentName}
                className="object-cover"
              />
              <AvatarFallback>
                {agentProfile.agentName.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <button
              type="button"
              className="absolute -bottom-1 -right-1 rounded-full border border-white bg-[#4989F7] p-1.5 text-white shadow hover:bg-[#3a78e6]"
              onClick={() => {
                setDraftImageUrl(agentProfile.profileImageUrl);
                setImageModalOpen(true);
              }}
              aria-label="Change profile image"
            >
              <Camera className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <p className="truncate text-base font-semibold text-[#111827]">
                {agentProfile.agentName.trim() || "AI Agent"}
              </p>
              <button
                type="button"
                className="shrink-0 text-xs font-medium text-[#4989F7] hover:text-[#3a78e6]"
                onClick={() => {
                  setDraftName(agentProfile.agentName);
                  setDraftTitle(agentProfile.jobTitle);
                  setProfileModalOpen(true);
                }}
              >
                Edit
              </button>
            </div>
            <p className="truncate text-sm text-gray-700">
              {agentProfile.jobTitle.trim() || "Assistant"}
            </p>
            <p className="truncate text-base font-semibold text-[#1f2937]">{businessName}</p>

            <div className="pt-2 text-sm">
              <div className="mt-1 flex items-center gap-2">
                <span className="truncate text-gray-700">
                  <span className="font-medium">Email:</span> {emailFromName} &lt;{notificationEmail}
                  &gt;
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <span className="flex items-center gap-1 text-gray-400 italic">
                  <span className="font-medium not-italic text-gray-700">Direct Line:</span>{" "}
                  Telephone Receptionist Disabled
                  <span
                    className="relative inline-flex items-center"
                    title="Enable Telephone Receptionist in Recommended Settings to provision a phone number."
                  >
                    <Info className="h-3 w-3 cursor-help text-gray-300" aria-hidden />
                  </span>
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2 truncate">
                <span className="font-medium text-gray-700">Website:</span>
                {normalizedWebsite ? (
                  <a
                    href={normalizedWebsite}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[#4989F7] hover:text-[#3a78e6]"
                  >
                    {websiteDisplay}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-gray-500">{websiteDisplay}</span>
                )}
              </div>

              <div className="mt-3 space-y-2 text-sm text-gray-600">
                {!hideInlineAbout ? (
                  <div className="flex items-start gap-2">
                    {isGeneratingPersona ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        <span>Generating persona from your website…</span>
                      </div>
                    ) : (
                      <>
                        <p className="whitespace-pre-wrap break-words">
                          <span className="font-medium text-gray-700">About:</span>{" "}
                          {agentProfile.persona?.trim() || "Not set"}
                        </p>
                        <button
                          type="button"
                          className="shrink-0 text-sm font-medium text-[#4989F7] hover:text-[#3a78e6]"
                          onClick={() => {
                            setDraftPersona(agentProfile.persona);
                            setPersonaModalOpen(true);
                          }}
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                ) : null}
                <div className="flex items-center gap-2">
                  <p className="truncate">
                    <span className="font-medium text-gray-700">Personality Traits:</span> Tone:
                    professional, Empathy: high, Style: direct
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <p className="truncate">
                    <span className="font-medium text-gray-700">Language & Communication:</span> EN
                    · Auto-Translation Off
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={profileModalOpen} onOpenChange={setProfileModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit profile details</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="preview-agent-name">Agent name</Label>
              <Input
                id="preview-agent-name"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="Victoria"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="preview-agent-title">Job title</Label>
              <Input
                id="preview-agent-title"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="Online Patient Coordinator"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setProfileModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                updateProfile({
                  agentName: draftName.trim(),
                  jobTitle: draftTitle.trim(),
                });
                setProfileModalOpen(false);
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={personaModalOpen} onOpenChange={setPersonaModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>About your agent</DialogTitle>
          </DialogHeader>
          <Textarea
            value={draftPersona}
            onChange={(e) => setDraftPersona(e.target.value)}
            rows={12}
            className="min-h-[240px] resize-y"
            placeholder="Describe how your agent introduces themselves and helps visitors..."
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPersonaModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                updateProfile({ persona: draftPersona });
                setPersonaModalOpen(false);
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Choose profile image</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <Avatar className="h-16 w-16 border border-slate-200">
                <AvatarImage src={draftImageUrl} alt={draftPresetName} className="object-cover" />
                <AvatarFallback>
                  {(draftName || "AG").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <label
                className="absolute -bottom-1 -right-1 cursor-pointer rounded-full border border-white bg-primary p-1.5 text-white shadow hover:bg-primary/90"
                aria-label="Upload profile image"
              >
                {isUploadingImage ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Camera className="h-3.5 w-3.5" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  disabled={isUploadingImage}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleImageUpload(file);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <Label>Profile image</Label>
              <Select value={selectValue} onValueChange={setDraftImageUrl}>
                <SelectTrigger>
                  <SelectValue placeholder="Select profile image">
                    <div className="flex items-center gap-2">
                      <Camera className="h-4 w-4 text-muted-foreground" aria-hidden />
                      <span>{draftPresetName}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {!draftPreset && draftImageUrl ? (
                    <SelectItem value={draftImageUrl}>
                      <div className="flex items-center gap-2">
                        <img
                          src={draftImageUrl}
                          alt=""
                          className="h-6 w-6 rounded-full object-cover"
                        />
                        <span>Custom upload</span>
                      </div>
                    </SelectItem>
                  ) : null}
                  {AGENT_PROFILE_PRESETS.map((preset) => (
                    <SelectItem key={preset.url} value={preset.url}>
                      <div className="flex items-center gap-2">
                        <img
                          src={preset.url}
                          alt=""
                          className="h-6 w-6 rounded-full object-cover"
                        />
                        <span>{parseAgentPresetLabel(preset.label).agentName}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Upload a photo or choose a default avatar.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setImageModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isUploadingImage || !draftImageUrl}
              onClick={() => {
                updateProfile({ profileImageUrl: draftImageUrl });
                setImageModalOpen(false);
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
